// Relatie-onboarding (klantvragenlijst) — zie docs/uploads/relatie-onboarding.md
//
// Gedeelde types tussen API, backoffice en portal voor de onboarding-flow:
// per-sectie formulierData, DTO's, diff-review en request-payloads.
//
// Enum-string-unions spiegelen de Prisma-enums (één waarheid). Bestaande
// domein-enums worden geïmporteerd i.p.v. her-gedeclareerd.

import { z } from 'zod'
import type { PaymentCondition, SlaLevel } from './index'
import type { ShiftType, PloegSoort, PackageType } from './werkbonnen'
import type { ProfielRichting, ProfielNiveau, SkillCategory } from './hrm'

// ─── Enums (spiegelen Prisma) ─────────────────────────────────────────────────

export type ClientOnboardingStatus =
  | 'CONCEPT'
  | 'WACHT_OP_KLANT'
  | 'IN_BEHANDELING'
  | 'WACHT_OP_REVIEW'
  | 'INTERN_AANVULLEN'
  | 'VOLTOOID'
  | 'GEANNULEERD'

// batch-59 onboarding-review (FO-437) — dezelfde negen categorieen als
// runtime-tuple, zodat het gedeelde review-schema (onderaan) er een `z.enum`
// van kan maken. De union hieronder wordt eruit afgeleid: één waarheid, dus een
// nieuwe categorie kan niet aan het schema ontsnappen.
export const CLIENT_ONBOARDING_CATEGORIE_VALUES = [
  'BEDRIJFSGEGEVENS',
  'CONTACTPERSONEN',
  'LOCATIES',
  'FACTURATIE',
  'WERKSCHEMA',
  // batch-61 klant-onboarding-docgen (FO-450 §2.1) - de D2-controleblokken die
  // nog geen sectie hadden: sluitingsdagen (rooster/pauze zitten in WERKSCHEMA,
  // facturatie in FACTURATIE). Prisma-enum ADD VALUE in
  // `20261231000710_b61_klant_onboarding_docgen_enums`.
  'SLUITINGSDAGEN',
  'SKILLPROFIEL',
  'VEILIGHEID',
  'TYPE_MONTEUR_SLA',
  'TARIEVEN',
] as const

export type ClientOnboardingCategorie =
  (typeof CLIENT_ONBOARDING_CATEGORIE_VALUES)[number]

export type ClientOnboardingSectieStatus =
  | 'TODO'
  | 'BEZIG'
  | 'AFGEROND'
  | 'OVERGESLAGEN'
  | 'AFGEWEZEN'

export type ClientOnboardingInvuller = 'KLANT' | 'INTERN'

export type VeiligheidsCategorie =
  | 'CERTIFICATEN'
  | 'TOEGANG'
  | 'PBM'
  | 'WERKVERGUNNING'
  | 'HUISREGELS'
  | 'MILIEU'
  | 'NOODPROCEDURES'
  | 'OVERIG'

// ─── Labels ──────────────────────────────────────────────────────────────────

export const CLIENT_ONBOARDING_STATUS_LABELS: Record<ClientOnboardingStatus, string> = {
  CONCEPT: 'Concept',
  WACHT_OP_KLANT: 'Wacht op klant',
  IN_BEHANDELING: 'In behandeling',
  WACHT_OP_REVIEW: 'Wacht op review',
  INTERN_AANVULLEN: 'Intern aanvullen',
  VOLTOOID: 'Voltooid',
  GEANNULEERD: 'Geannuleerd',
}

export const CLIENT_ONBOARDING_CATEGORIE_LABELS: Record<ClientOnboardingCategorie, string> = {
  BEDRIJFSGEGEVENS: 'Bedrijfsgegevens',
  CONTACTPERSONEN: 'Contactpersonen',
  LOCATIES: 'Locaties',
  FACTURATIE: 'Facturatie',
  WERKSCHEMA: 'Werkschema',
  SLUITINGSDAGEN: 'Sluitingsdagen',
  SKILLPROFIEL: 'Monteurprofiel',
  VEILIGHEID: 'Veiligheidsvoorschriften',
  TYPE_MONTEUR_SLA: 'Type monteur & SLA',
  TARIEVEN: 'Tarieven',
}

export const VEILIGHEIDS_CATEGORIE_LABELS: Record<VeiligheidsCategorie, string> = {
  CERTIFICATEN: 'Certificaten',
  TOEGANG: 'Toegang',
  PBM: 'PBM',
  WERKVERGUNNING: 'Werkvergunning',
  HUISREGELS: 'Huisregels',
  MILIEU: 'Milieu',
  NOODPROCEDURES: 'Noodprocedures',
  OVERIG: 'Overig',
}

// ─── Sectie-volgorde (één waarheid voor wizard + seeding) ────────────────────

export interface OnboardingSectieDefinitie {
  categorie: ClientOnboardingCategorie
  volgorde: number
  wieVult: ClientOnboardingInvuller
}

// Volgorde 1..8 = klant-secties (wizard); 9..10 = intern (alleen backoffice).
// batch-61 (FO-450): SLUITINGSDAGEN erbij op 6; bestaande onboardings zonder die
// rij krijgen hem lazy (`zorgKlantSectiesCompleet`) - de volgorde-kolom van
// oudere rijen blijft staan (de wizard sorteert op zijn eigen stappenlijst).
export const ONBOARDING_SECTIES: readonly OnboardingSectieDefinitie[] = [
  { categorie: 'BEDRIJFSGEGEVENS', volgorde: 1, wieVult: 'KLANT' },
  { categorie: 'CONTACTPERSONEN', volgorde: 2, wieVult: 'KLANT' },
  { categorie: 'LOCATIES', volgorde: 3, wieVult: 'KLANT' },
  { categorie: 'FACTURATIE', volgorde: 4, wieVult: 'KLANT' },
  { categorie: 'WERKSCHEMA', volgorde: 5, wieVult: 'KLANT' },
  { categorie: 'SLUITINGSDAGEN', volgorde: 6, wieVult: 'KLANT' },
  { categorie: 'SKILLPROFIEL', volgorde: 7, wieVult: 'KLANT' },
  { categorie: 'VEILIGHEID', volgorde: 8, wieVult: 'KLANT' },
  { categorie: 'TYPE_MONTEUR_SLA', volgorde: 9, wieVult: 'INTERN' },
  { categorie: 'TARIEVEN', volgorde: 10, wieVult: 'INTERN' },
]

export const KLANT_SECTIE_CATEGORIEEN: readonly ClientOnboardingCategorie[] =
  ONBOARDING_SECTIES.filter((s) => s.wieVult === 'KLANT').map((s) => s.categorie)

export const INTERNE_SECTIE_CATEGORIEEN: readonly ClientOnboardingCategorie[] =
  ONBOARDING_SECTIES.filter((s) => s.wieVult === 'INTERN').map((s) => s.categorie)

// ─── formulierData per sectie ────────────────────────────────────────────────

export interface BedrijfsgegevensData {
  naam: string
  kvkNummer: string
  btwNummer: string
  branche: string
  iban: string
  straat: string
  huisnummer: string
  postcode: string
  plaats: string
  land: string
  email: string
  email2?: string
  telefoon: string
  telefoon2?: string
  // Logo wordt vooraf geüpload; hier alleen de storage-key (shared/server-safe,
  // dus geen DOM `File`).
  logoStorageKey?: string
  // Google geocoding (optioneel — server leidt lat/long server-side af).
  placeId?: string
}

export type ContactpersoonRol = 'PLANNING' | 'FACTUREN' | 'OPERATIONEEL' | 'PORTAAL_ADMIN'

export const CONTACTPERSOON_ROL_LABELS: Record<ContactpersoonRol, string> = {
  PLANNING: 'Planning',
  FACTUREN: 'Facturen',
  OPERATIONEEL: 'Operationeel',
  PORTAAL_ADMIN: 'Portaalbeheerder',
}

export interface ContactpersoonInput {
  voornaam: string
  achternaam: string
  email: string
  telefoon?: string
  mobiel?: string
  functie?: string
  afdeling?: string
  rollen: ContactpersoonRol[]
}

export interface ContactpersonenData {
  contacten: ContactpersoonInput[]
}

export interface LocatieInput {
  naam: string
  straat: string
  huisnummer: string
  postcode: string
  plaats: string
  land: string
  isPrimair: boolean
  telefoon?: string
  email?: string
  notities?: string
  placeId?: string
}

export interface LocatiesData {
  locaties: LocatieInput[]
}

export interface FacturatieData {
  factuurEmail: string
  factuurCcEmail?: string
  betaaltermijn: PaymentCondition
  btwVerlegd: boolean
  heeftGRekening: boolean
  gRekeningPercentage?: number
  poNummerVerplicht: boolean
  notities?: string
}

// Eén dienst in het werkschema. Sleutels spiegelen ClientWorkSchedule.diensten
// én WorkScheduleEditor (startTime/endTime/breakMinutes) — één waarheid.
export interface WerkschemaDienst {
  type: PloegSoort
  startTime: string // "HH:MM"
  endTime: string // "HH:MM"
  /**
   * Feitelijke pauze in minuten. FO-473 (batch-63): `null` = niet ingevuld en
   * wordt nooit als 0 gelezen; de planning valt dan terug op de standaard, met
   * zichtbare herkomst.
   */
  breakMinutes: number | null
}

export interface WerkschemaData {
  shiftType: ShiftType
  werkschema: {
    naam: string
    diensten: WerkschemaDienst[]
    rotatiePatroon?: string[]
    rotatieStartDatum?: string // ISO yyyy-MM-dd
    rotatieCyclusDagen?: number
  }
  toelichting?: string
}

export interface TypeMonteurSlaData {
  typeMonteur: string
  slaLevel?: SlaLevel
  interneNotities?: string
}

// batch-61 klant-onboarding-docgen (FO-450) - sluitingsdagen als sectie. Spiegelt
// `ClientClosedPeriod` (naam, start/eind als kalenderdag, jaarlijks) in de vorm
// die de klant in de wizard invult; bij voltooien worden alleen nog niet
// bestaande periodes aangemaakt (match op naam + datums).
export interface SluitingsdagInput {
  naam: string
  /** ISO kalenderdag yyyy-MM-dd (Europe/Amsterdam). */
  startDatum: string
  eindDatum: string
  jaarlijks: boolean
  toelichting?: string
}

export interface SluitingsdagenData {
  sluitingsdagen: SluitingsdagInput[]
  /** Expliciet "wij hebben geen vaste sluitingsdagen" - een lege lijst is dan een antwoord. */
  geenSluitingsdagen?: boolean
}

/**
 * batch-61 klant-onboarding-docgen (FO-450) - HET gedeelde request-schema voor
 * de SLUITINGSDAGEN-sectie. Eén bron voor de publieke token-route, de
 * backoffice-route en de wizard, zodat het formulier niets kan versturen dat de
 * API weigert (CLAUDE.md § Validatie & foutmeldingen).
 *
 * De regel die alleen client-side stond en nu ook server-side geldt: een lege
 * lijst is pas een antwoord mét het expliciete "wij hebben geen vaste
 * sluitingsdagen"-vinkje. Zonder die refine kon een PUT met `{ sluitingsdagen:
 * [] }` de sectie op AFGEROND zetten zonder dat er iets beantwoord was.
 */
export const sluitingsdagSchema = z
  .object({
    naam: z.string().trim().min(1).max(120),
    startDatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    eindDatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    jaarlijks: z.boolean().default(false),
    toelichting: z.string().max(2000).optional(),
  })
  .refine((v) => v.eindDatum >= v.startDatum, {
    path: ['eindDatum'],
    message: 'De einddatum ligt vóór de startdatum.',
  })

export const sluitingsdagenSchema = z
  .object({
    sluitingsdagen: z.array(sluitingsdagSchema).max(100),
    geenSluitingsdagen: z.boolean().optional(),
  })
  .passthrough()
  .refine((v) => v.sluitingsdagen.length > 0 || v.geenSluitingsdagen === true, {
    path: ['sluitingsdagen'],
    message: 'Voeg een sluitingsperiode toe, of geef aan dat er geen vaste sluitingsdagen zijn.',
  })

export type VaardigheidBelang = 'NICE_TO_HAVE' | 'BELANGRIJK' | 'VEREIST'

export const VAARDIGHEID_BELANG_LABELS: Record<VaardigheidBelang, string> = {
  NICE_TO_HAVE: 'Nice to have',
  BELANGRIJK: 'Belangrijk',
  VEREIST: 'Vereist',
}

export interface GewensteVaardigheid {
  categorie: SkillCategory
  belang: VaardigheidBelang
}

export interface SkillprofielInput {
  /**
   * FO-367 (batch-42) — één of meer gewenste richtingen (multi-select). Minimaal
   * één; server-side afgedwongen. Vervangt het oude enkelvoudige `richting`.
   */
  richtingen: ProfielRichting[]
  /**
   * @deprecated Oud enkelvoudig veld. Alleen nog aanwezig voor het lezen van
   * pre-batch-42 payloads (compat via `leesProfielRichtingen`); nieuwe payloads
   * vullen `richtingen`.
   */
  richting?: ProfielRichting
  niveau: ProfielNiveau
  aantalGewenst?: number
  gewensteVaardigheden?: GewensteVaardigheid[]
  specialisatieOmschrijving?: string
  toelichting?: string
}

/**
 * Lees de richting(en) uit een (mogelijk oude) skillprofiel-input: nieuw
 * `richtingen`-array wint; valt terug op het enkelvoudige `richting` van
 * pre-batch-42 payloads. Geeft een lege lijst als niets is ingevuld (de UI +
 * server-Zod dwingen ≥ 1 af).
 */
export function leesProfielRichtingen(p: {
  richtingen?: ProfielRichting[] | null
  richting?: ProfielRichting | null
}): ProfielRichting[] {
  if (Array.isArray(p.richtingen) && p.richtingen.length > 0) return p.richtingen
  if (p.richting) return [p.richting]
  return []
}

export interface SkillprofielData {
  profielen: SkillprofielInput[]
}

export interface VeiligheidVoorschriftDocument {
  bestandsnaam: string
  storageKey: string
}

export interface VeiligheidVoorschrift {
  categorie: VeiligheidsCategorie
  naam: string
  beschrijving?: string
  isVerplicht: boolean
  document?: VeiligheidVoorschriftDocument
}

export interface VeiligheidAlgemeenChecklist {
  vcaCertificaatVerplicht: boolean
  ehboCertificaatVerplicht: boolean
  toegangspasNodig: boolean
  werkvergunningNodig: boolean
  pbmVerplichtingen: string[]
  bhvInstructieNodig: boolean
  specifiekeMilieuRegels: boolean
}

export interface VeiligheidData {
  voorschriften: VeiligheidVoorschrift[]
  algemeenChecklist: VeiligheidAlgemeenChecklist
  aanvullendeVoorschriften?: string
}

// Gangbare PBM's voor de multi-select in de wizard.
export const PBM_OPTIES: readonly string[] = [
  'Helm',
  'Veiligheidsbril',
  'Veiligheidsschoenen',
  'Werkhandschoenen',
  'Gehoorbescherming',
  'Valbeveiliging',
  'Ademhalingsbescherming',
  'Veiligheidsvest',
]

// Bedragen als `number` (transport/JSON); de service converteert naar Prisma
// `Decimal` bij het schrijven naar ClientTariff.
export interface TariefInput {
  naam: string
  pakketType: PackageType
  uurtarief: number // Basistarief (€/uur)
  overuurtarief?: number
  kmVergoeding?: number // Kmvergoeding (€/km)
  reisUurtarief?: number // Reisurentarief (€/uur) - I.5
  shiftType: ShiftType
  isStandaard: boolean
  geldigVanaf?: string // ISO yyyy-MM-dd
}

export interface TarievenData {
  tarieven: TariefInput[]
}

// Categorie → bijbehorende formulierData-vorm.
export interface OnboardingFormulierDataMap {
  BEDRIJFSGEGEVENS: BedrijfsgegevensData
  CONTACTPERSONEN: ContactpersonenData
  LOCATIES: LocatiesData
  FACTURATIE: FacturatieData
  WERKSCHEMA: WerkschemaData
  SLUITINGSDAGEN: SluitingsdagenData
  SKILLPROFIEL: SkillprofielData
  VEILIGHEID: VeiligheidData
  TYPE_MONTEUR_SLA: TypeMonteurSlaData
  TARIEVEN: TarievenData
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface ClientOnboardingSectieDTO {
  id: string
  categorie: ClientOnboardingCategorie
  volgorde: number
  status: ClientOnboardingSectieStatus
  wieVult: ClientOnboardingInvuller
  // Vorm afhankelijk van `categorie` (zie OnboardingFormulierDataMap). `null`
  // zolang de sectie nog niet is ingevuld.
  formulierData: unknown
  reviewNotitie: string | null
  reviewdOp: string | null
  voltooidOp: string | null
}

export interface ClientOnboardingDTO {
  id: string
  clientId: string
  clientNaam: string
  status: ClientOnboardingStatus
  expiresAt: string
  aangemaakt: string
  aangemaaktDoor: string
  voltooidOp: string | null
  deadline: string | null
  voortgang: number // 0.0 .. 1.0
  // Opdracht-placeholders (I.7) voor documentgeneratie. UTC ISO-strings; UI
  // converteert naar Europe/Amsterdam. `opdrachtEindDatum` leeg = onbepaald.
  opdrachtStartDatum: string | null
  opdrachtEindDatum: string | null
  referentienummer: string | null
  secties: ClientOnboardingSectieDTO[]
}

// Lijst-item voor het backoffice-overzicht (`/relatie-onboarding`).
export interface ClientOnboardingListItemDTO {
  id: string
  clientId: string
  clientNaam: string
  status: ClientOnboardingStatus
  voortgang: number
  deadline: string | null
  aangemaakt: string
  expiresAt: string
}

// Deel-link info. ALLEEN in backoffice-context (authed + CRM-permissie)
// teruggeven — NOOIT in publieke token-responses.
export interface OnboardingShareInfo {
  token: string
  url: string
  expiresAt: string
}

// ─── Diff-review (voltooien bij bestaande klant) ─────────────────────────────

// Client-velden die financieel zijn → in diff-review standaard UIT. Eén waarheid
// voor de service (diff-berekening) én de UI (rode badge). Keys = Client-kolomnamen.
export const FINANCIELE_VELDEN = [
  'iban',
  'vatNumber',
  'invoiceEmail',
  'invoiceCcEmail',
  'paymentCondition',
  'hasGAccount',
  'gAccountPercentage',
  'vatReversed',
] as const

export type FinancieelVeld = (typeof FINANCIELE_VELDEN)[number]

export interface DiffVeld {
  key: string // Client-kolomnaam
  label: string
  huidig: unknown
  nieuw: unknown
  isGewijzigd: boolean
  isFinancieel: boolean
  defaultOvernemen: boolean
  // I.2a: dit veld is bij het starten vanuit een BESTAANDE relatie voorgevuld
  // uit het profiel ("ter controle"). De UI badget zulke velden zodat de
  // reviewer ze bevestigt/corrigeert. Afgeleid uit `_overgenomenVelden` in de
  // sectie-formulierData.
  isOvergenomen: boolean
}

export interface OnboardingDiffCollectie {
  categorie: ClientOnboardingCategorie
  label: string
  aantal: number // aantal records dat aangemaakt wordt (collecties krijgen geen per-veld diff)
}

export interface OnboardingDiff {
  onboardingId: string
  clientId: string
  isConceptShell: boolean
  velden: DiffVeld[]
  collecties: OnboardingDiffCollectie[]
}

// ─── Request-payloads ────────────────────────────────────────────────────────

export interface CreateClientOnboardingInput {
  // Bestaande klant → `clientId`. Nieuwe klant → `nieuwKlantNaam` (PAD A:
  // er wordt eerst een Client-shell met status CONCEPT aangemaakt). Precies één.
  clientId?: string
  nieuwKlantNaam?: string
  deadline?: string // ISO yyyy-MM-dd
}

// Opdracht-placeholders bijwerken op de onboarding (I.7). ISO yyyy-MM-dd voor
// de datums (server slaat UTC-middernacht op); `null` wist het veld expliciet,
// `undefined`/ontbrekend laat het ongemoeid. `referentienummer` landt in de
// `placeholderWaarden`-JSON (geen tarief, geen eigen kolom).
export interface UpdateOpdrachtVeldenInput {
  opdrachtStartDatum?: string | null
  opdrachtEindDatum?: string | null
  referentienummer?: string | null
}

export interface VoltooiClientOnboardingInput {
  // Client-kolomnamen die overgenomen mogen worden. Bij een CONCEPT-shell
  // genegeerd (alle data wordt direct geschreven).
  goedgekeurdeVelden: string[]
}

export interface ReviewSectieInput {
  goedgekeurd: boolean
  notitie?: string
}

// ─── batch-59 onboarding-review (FO-437) — gedeeld review-request-schema ─────
//
// EEN bron voor de sectie-review: de API parset er zijn request mee en de
// backoffice-formulieren (relatie-detail Onboarding-tab + het Postvak-blok)
// valideren er client-side mee vóór verzturen. Zo kan het formulier per
// definitie niets sturen dat de API weigert (CLAUDE.md, "Validatie &
// foutmeldingen").
//
// Harde regel uit de spec (§6 / T3): AFKEUREN VEREIST EEN REDEN. Zonder reden
// weet de klant niet wat hij moet aanpassen en komt de onboarding alsnog stil
// te staan - precies het probleem dat dit FO oplost. De issue landt op het
// `notitie`-pad zodat de UI hem inline onder het tekstvak toont.
// Goedkeuren houdt de notitie optioneel (§A5-lijn: goedkeur-opmerking mag).

export const ONBOARDING_AFKEUR_REDEN_MIN = 3

const reviewNotitie = z.string().trim().max(2000).optional()

/** Body van `POST /client-onboarding/:id/review` (één sectie beoordelen). */
export const clientOnboardingReviewSchema = z
  .object({
    categorie: z.enum(CLIENT_ONBOARDING_CATEGORIE_VALUES),
    goedgekeurd: z.boolean(),
    notitie: reviewNotitie,
  })
  .superRefine((v, ctx) => {
    if (!v.goedgekeurd && (v.notitie ?? '').length < ONBOARDING_AFKEUR_REDEN_MIN) {
      ctx.addIssue({
        code: 'custom',
        path: ['notitie'],
        message: `Geef een reden van minimaal ${ONBOARDING_AFKEUR_REDEN_MIN} tekens - de relatie moet weten wat er aangepast moet worden.`,
      })
    }
  })

export type ClientOnboardingReviewInput = z.infer<typeof clientOnboardingReviewSchema>

/**
 * Body van `POST /client-onboarding/:id/review-openstaand` - beoordeel in één
 * keer élke klant-sectie die na de laatste indiening nog openstaat (aanname A2:
 * "keur alles goed" mag als extra, met dezelfde audit). Afkeuren blijft ook hier
 * reden-plichtig.
 */
export const clientOnboardingReviewOpenstaandSchema = z
  .object({
    goedgekeurd: z.boolean(),
    notitie: reviewNotitie,
  })
  .superRefine((v, ctx) => {
    if (!v.goedgekeurd && (v.notitie ?? '').length < ONBOARDING_AFKEUR_REDEN_MIN) {
      ctx.addIssue({
        code: 'custom',
        path: ['notitie'],
        message: `Geef een reden van minimaal ${ONBOARDING_AFKEUR_REDEN_MIN} tekens - de relatie moet weten wat er aangepast moet worden.`,
      })
    }
  })

export type ClientOnboardingReviewOpenstaandInput = z.infer<
  typeof clientOnboardingReviewOpenstaandSchema
>

export type OnboardingLocale = 'nl' | 'en'
